import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Box,
  List,
  Divider,
  SkeletonDisplayText,
  SkeletonBodyText,
  Banner,
} from '@shopify/polaris';
import type { DashboardStats } from '../types/api';

/**
 * Common failure reason with count
 */
export interface FailureReason {
  /** Reason description */
  reason: string;
  /** Number of occurrences */
  count: number;
}

export interface FailureRateCardProps {
  /** Dashboard stats data */
  stats: DashboardStats | null;
  /** Optional list of common failure reasons */
  failureReasons?: FailureReason[];
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
}

/**
 * Get badge tone based on failure count
 */
function getFailureTone(
  failedCount: number,
  totalCount: number
): 'success' | 'warning' | 'critical' {
  if (totalCount === 0) return 'success';
  const failureRate = (failedCount / totalCount) * 100;
  if (failureRate <= 2) return 'success';
  if (failureRate <= 10) return 'warning';
  return 'critical';
}

/**
 * Default failure reasons when actual data is not available
 * These are common IndexNow/Google API failure scenarios
 */
const DEFAULT_FAILURE_REASONS: FailureReason[] = [
  { reason: 'Rate limit exceeded (429)', count: 0 },
  { reason: 'Invalid URL format', count: 0 },
  { reason: 'Network timeout', count: 0 },
  { reason: 'Service unavailable (503)', count: 0 },
];

/**
 * Loading skeleton for FailureRateCard
 */
function FailureRateCardSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonBodyText lines={1} />
        <InlineStack gap="400" align="start">
          <Box minWidth="100px">
            <BlockStack gap="200">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={1} />
            </BlockStack>
          </Box>
        </InlineStack>
        <Divider />
        <SkeletonBodyText lines={3} />
      </BlockStack>
    </Card>
  );
}

/**
 * Card displaying failure count and common failure reasons.
 *
 * Features:
 * - Shows total failures for today
 * - Displays failure count with color-coded badge
 * - Lists common failure reasons with counts
 * - Shows breakdown by service (IndexNow vs Google)
 * - Loading skeleton state
 * - Error banner for fetch failures
 *
 * @example
 * ```tsx
 * <FailureRateCard
 *   stats={dashboardStats}
 *   failureReasons={[
 *     { reason: 'Rate limit exceeded', count: 3 },
 *     { reason: 'Network timeout', count: 1 },
 *   ]}
 *   loading={isLoading}
 *   error={errorMessage}
 * />
 * ```
 */
export function FailureRateCard({
  stats,
  failureReasons,
  loading = false,
  error = null,
}: FailureRateCardProps) {
  if (loading) {
    return <FailureRateCardSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Failures
          </Text>
          <Banner tone="critical">
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Failures
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            No data available
          </Text>
        </BlockStack>
      </Card>
    );
  }

  const { today } = stats;
  const failedCount = today.failedPings;
  const totalCount = today.totalPings;
  const failureTone = getFailureTone(failedCount, totalCount);

  // Calculate failure rate percentage
  const failureRate = totalCount > 0
    ? Math.round((failedCount / totalCount) * 100 * 10) / 10
    : 0;

  // Use provided failure reasons or defaults
  const reasons = failureReasons && failureReasons.length > 0
    ? failureReasons
    : DEFAULT_FAILURE_REASONS;

  // Filter to only show reasons with counts > 0 (if we have real data)
  const activeReasons = failureReasons
    ? reasons.filter((r) => r.count > 0)
    : [];

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h3" variant="headingSm" tone="subdued">
          Failures
        </Text>

        {/* Main failure count */}
        <InlineStack gap="600" align="start" wrap>
          <Box minWidth="100px">
            <BlockStack gap="100">
              <Text as="p" variant="headingXl" fontWeight="bold">
                {failedCount}
              </Text>
              <InlineStack gap="100" blockAlign="center">
                <Badge tone={failureTone}>
                  {`${failureRate}% failure rate`}
                </Badge>
              </InlineStack>
            </BlockStack>
          </Box>
        </InlineStack>

        {/* No failures message */}
        {failedCount === 0 && (
          <Banner tone="success">
            No failures today. All pings were successful.
          </Banner>
        )}

        {/* Failure reasons list */}
        {failedCount > 0 && activeReasons.length > 0 && (
          <>
            <Divider />
            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">
                Common reasons
              </Text>
              <List>
                {activeReasons.slice(0, 4).map((reason, index) => (
                  <List.Item key={index}>
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" variant="bodyMd">
                        {reason.reason}
                      </Text>
                      <Badge>{String(reason.count)}</Badge>
                    </InlineStack>
                  </List.Item>
                ))}
              </List>
            </BlockStack>
          </>
        )}

        {/* Hint when there are failures but no detailed reasons */}
        {failedCount > 0 && activeReasons.length === 0 && (
          <>
            <Divider />
            <Text as="p" variant="bodySm" tone="subdued">
              Check the ping logs for detailed failure information.
            </Text>
          </>
        )}
      </BlockStack>
    </Card>
  );
}
